import React, { useState, useEffect, useRef } from 'react';
import { GripVertical, Calendar, Edit2, Copy, MapPin, Clock, CheckCircle2, XCircle, AlertCircle, StickyNote, Ban, PenLine, ThumbsUp, ImageIcon, MessageCircle } from 'lucide-react';
import { formatET } from '../lib/timezone';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_COLORS = {
  instagram: 'bg-pink-100 text-pink-700',
  facebook: 'bg-blue-100 text-blue-700',
  twitter: 'bg-sky-100 text-sky-700',
  linkedin: 'bg-indigo-100 text-indigo-700',
  tiktok: 'bg-slate-100 text-slate-700',
  general: 'bg-[#0066CC]/10 text-[#0066CC]'
};

const APPROVAL_BADGES = {
  do_not_run: { icon: Ban, label: 'Do Not Run', style: 'bg-red-100 text-red-700 border border-red-200' },
  approved_with_edits: { icon: PenLine, label: 'Edits Requested', style: 'bg-blue-100 text-blue-700 border border-blue-200' },
  approved: { icon: ThumbsUp, label: 'Approved', style: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  changes_requested: { icon: AlertCircle, label: 'Changes Requested', style: 'bg-orange-100 text-orange-700 border border-orange-200' },
};

const STATUS_COLORS = {
  draft: 'bg-[#1a3d5c]/10 text-[#1a3d5c]/70',
  scheduled: 'bg-[#0066CC]/20 text-[#0066CC]',
  published: 'bg-green-100 text-green-700'
};

const WORKFLOW_COLORS = {
  draft: { dot: 'bg-slate-400', badge: 'bg-slate-100 text-slate-600' },
  client_idea: { dot: 'bg-purple-500', badge: 'bg-purple-100 text-purple-600' },
  in_review: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-600' },
  scheduled: { dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-600' },
  ready_to_post: { dot: 'bg-green-500', badge: 'bg-green-100 text-green-600' },
  posted: { dot: 'bg-emerald-600', badge: 'bg-emerald-100 text-emerald-700' }
};

const WORKFLOW_LABELS = {
  draft: 'Draft',
  client_idea: 'Client Idea',
  in_review: 'In Review',
  scheduled: 'Scheduled',
  ready_to_post: 'Ready',
  posted: 'Posted'
};

const formatTime12h = (time24) => {
  if (!time24) return null;
  const [h, m] = time24.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

function PostThumb({ src }) {
  const ref = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const isExternal = src && src.startsWith('http');
  const isInternal = src && !isExternal;

  useEffect(() => {
    const el = ref.current;
    if (!el || !isInternal) return;
    let cancelled = false;

    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          obs.disconnect();
          const slug = localStorage.getItem('activeClientSlug') || '';
          fetch(`${API}${src}`, { credentials: 'include', headers: { 'X-Client-Id': slug } })
            .then(r => { if (!r.ok) throw new Error(r.status); return r.blob(); })
            .then(blob => { if (!cancelled) setBlobUrl(URL.createObjectURL(blob)); })
            .catch(() => {});
        }
      },
      { rootMargin: '100px' }
    );
    obs.observe(el);
    return () => { cancelled = true; obs.disconnect(); };
  }, [src, isInternal]);

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const imgSrc = isExternal ? src : blobUrl;

  return (
    <div ref={ref} className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-testid="post-thumb">
      {imgSrc ? (
        <img src={imgSrc} alt="" className="w-full h-full object-cover" draggable={false} />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="w-5 h-5 text-gray-300" />
        </div>
      )}
    </div>
  );
}

function PublishIssue({ post }) {
  const isError = ['failed', 'missed'].includes(post.publish_status) && post.publish_error;
  const text = isError ? post.publish_error : post.publish_warning;
  if (!text || post.publish_status === 'published') return null;
  return (
    <div
      className={`mt-2 flex items-start gap-1.5 text-xs rounded-md px-2 py-1.5 border ${isError ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}
      data-testid={`publish-issue-${post.id}`}
    >
      <AlertCircle className="w-3.5 h-3.5 mt-px flex-shrink-0" />
      <span>{isError ? text : `Not publishing yet: ${text}`}</span>
    </div>
  );
}

export function PostCard({ post, onClick, dragHandleProps, isDragging, onDuplicate, readOnly }) {
  const getFormattedDate = () => {
    if (!post.scheduled_date) return 'No date';
    return formatET(post.scheduled_date, 'MMM d, yyyy') || 'Invalid date';
  };
  const formattedDate = getFormattedDate();

  const ws = post.workflow_status || 'draft';
  const workflowStyle = WORKFLOW_COLORS[ws] || WORKFLOW_COLORS.draft;

  // Resolve thumbnail URL from media_urls or image_url
  const thumbUrl = (post.media_urls && post.media_urls.length > 0)
    ? (post.media_urls[0].includes('/library/images/')
        ? post.media_urls[0].replace(/\/file$/, '/thumbnail')
        : post.media_urls[0])
    : post.image_url || null;

  return (
    <div
      className={`post-card flex items-start gap-3 cursor-pointer ${isDragging ? 'dragging' : ''}`}
      onClick={onClick}
      data-testid={`post-card-${post.id}`}
    >
      {/* Workflow Status Indicator */}
      <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${workflowStyle.dot}`} />

      {/* Drag Handle */}
      {!readOnly && dragHandleProps && (
        <div
          {...dragHandleProps}
          className="flex-shrink-0 p-1 text-[#1a3d5c]/30 hover:text-[#1a3d5c]/60 cursor-grab active:cursor-grabbing"
          onClick={(e) => e.stopPropagation()}
          data-testid={`post-drag-handle-${post.id}`}
        >
          <GripVertical className="w-5 h-5" />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="font-semibold text-[#1a3d5c] truncate">{post.title}</h4>
          <div className="flex items-center gap-1 flex-shrink-0">
            {!readOnly && onDuplicate && (
              <button
                onClick={(e) => { e.stopPropagation(); onDuplicate(post); }}
                className="p-1.5 rounded-lg text-[#1a3d5c]/30 hover:text-[#2563eb] hover:bg-[#eff6ff] transition-colors"
                title="Duplicate post"
                data-testid={`duplicate-post-${post.id}`}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            )}
            {!readOnly && <Edit2 className="w-4 h-4 text-[#1a3d5c]/30 flex-shrink-0" />}
          </div>
        </div>

        {post.content && (
          <p className="text-sm text-[#1a3d5c]/60 line-clamp-2 mb-3">
            {post.content}
          </p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {/* Date */}
          <span className="flex items-center gap-1 text-xs text-[#1a3d5c]/50">
            <Calendar className="w-3 h-3" />
            {formattedDate}
            {post.scheduled_time && (
              <span className="ml-1 text-[#1a3d5c]/70 font-medium">
                {formatTime12h(post.scheduled_time)}
              </span>
            )}
          </span>

          {/* Publish Status */}
          {post.publish_status === 'published' ? (
            <span className="flex items-center gap-1 text-xs text-green-600 font-medium" data-testid={`publish-status-${post.id}`}>
              <CheckCircle2 className="w-3 h-3" /> Published
            </span>
          ) : post.publish_status === 'failed' || post.publish_status === 'missed' ? (
            <span className="flex items-center gap-1 text-xs text-red-500 font-medium" data-testid={`publish-status-${post.id}`}>
              <XCircle className="w-3 h-3" /> {post.publish_status === 'missed' ? 'Missed' : 'Failed'}
            </span>
          ) : post.workflow_status === 'posted' || post.published_at ? (
            <span className="flex items-center gap-1 text-xs text-green-600 font-medium" data-testid={`publish-status-${post.id}`}>
              <CheckCircle2 className="w-3 h-3" /> Published
            </span>
          ) : null}

          {/* Location */}
          {post.location && (
            <span className="flex items-center gap-1 text-xs text-[#1a3d5c]/50" data-testid={`post-location-${post.id}`}>
              <MapPin className="w-3 h-3 text-rose-400" />
              <span className="truncate max-w-[140px]">{post.location}</span>
            </span>
          )}

          {/* Post Type Badge */}
          {post.post_type && (
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-600">
              {post.post_type}
            </span>
          )}

          {/* Platform Badge */}
          <span className={`text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded ${PLATFORM_COLORS[post.platform] || PLATFORM_COLORS.general}`}>
            {post.platform}
          </span>

          {/* Workflow Status Badge */}
          <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded ${workflowStyle.badge}`}>
            {WORKFLOW_LABELS[ws] || ws}
          </span>

          {/* Client Approval Badge */}
          {post.approval_status && APPROVAL_BADGES[post.approval_status] && (() => {
            const badge = APPROVAL_BADGES[post.approval_status];
            const Icon = badge.icon;
            return (
              <span
                className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${badge.style}`}
                data-testid={`approval-badge-${post.id}`}
              >
                <Icon className="w-3 h-3" /> {badge.label}
              </span>
            );
          })()}
        </div>

        <PublishIssue post={post} />

        {/* Visual Notes */}
        {post.visual_notes && (
          <p className="text-xs text-[#1a3d5c]/40 mt-2 italic line-clamp-1">
            Visual: {post.visual_notes}
          </p>
        )}

        {/* Agency Notes Indicator */}
        {post.agency_notes && (
          <p className="text-xs text-amber-500/70 mt-2 italic line-clamp-1 flex items-center gap-1" data-testid={`agency-note-${post.id}`}>
            <StickyNote className="w-3 h-3" />
            {post.agency_notes}
          </p>
        )}

        {/* Client Feedback */}
        {post.approval_status && post.approval_status !== 'pending' && (
          <div
            className={`mt-2 px-2.5 py-1.5 rounded-md text-xs border ${
              post.approval_status === 'approved'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : post.approval_status === 'approved_with_edits'
                ? 'bg-amber-50 border-amber-200 text-amber-700'
                : post.approval_status === 'do_not_run'
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-gray-50 border-gray-200 text-gray-600'
            }`}
            data-testid={`client-feedback-${post.id}`}
          >
            <div className="flex items-center gap-1.5 font-semibold mb-0.5">
              <MessageCircle className="w-3 h-3" />
              {post.approval_status === 'approved' && 'Client Approved'}
              {post.approval_status === 'approved_with_edits' && 'Client Approved with Edits'}
              {post.approval_status === 'do_not_run' && 'Client: Do Not Run'}
            </div>
            {(() => {
              const note = post.approval_note
                || (post.client_feedback?.length > 0
                    ? post.client_feedback[post.client_feedback.length - 1]?.comment
                    : null);
              return note ? (
                <p className="line-clamp-2 leading-relaxed" data-testid={`client-note-${post.id}`}>
                  "{note}"
                </p>
              ) : null;
            })()}
          </div>
        )}
      </div>

      {/* Image Thumbnail */}
      {thumbUrl && <PostThumb src={thumbUrl} />}
    </div>
  );
}

// Compact card for calendar view with color coding
export function PostDot({ post }) {
  const ws = post.workflow_status || 'draft';
  const workflowStyle = WORKFLOW_COLORS[ws] || WORKFLOW_COLORS.draft;
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${workflowStyle.dot}`} title={WORKFLOW_LABELS[ws]} />
  );
}

export { WORKFLOW_COLORS, WORKFLOW_LABELS };
